/* eslint-disable unused-imports/no-unused-vars */
import { useState, useCallback, useMemo, ChangeEvent } from 'react';

import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import templateLiteralToHTML from 'utils/templateLiteralToHTML';

import InputTooltip from './InputTooltip';
import masks from './masks';
import {
  Container,
  Error,
  Label,
  IconContainer,
  PasswordIconContainer,
  FieldRulesContainer,
  PreText,
} from './styles';
import { ICustomInputProps, IMaskProps } from './types';

const CustomInput: React.FC<ICustomInputProps> = (props) => {
  const {
    error,
    label = null,
    icon = false,
    type = 'text',
    disabled = false,
    isRequired = false,
    fieldRules,
    preText,
    id,
    mask,
    value,
    className,
    autoComplete = 'off',
    onChange = () => {},
    tooltip,
    placeholder,
    isHTMLLabel = false,
  } = props;

  const [inputType, setInputType] = useState<string>(type);
  const [inputOnFocus, setInputOnFocus] = useState<boolean>(false);

  const inputMask = useMemo(
    (): IMaskProps => ({
      middleware: mask ? masks[mask] : (val: string) => val,
    }),
    [mask],
  );

  const handleOnChange = useCallback(
    (e: ChangeEvent<any>) => {
      if (!e) return;

      if (!mask) return onChange(e);

      const newEvent = e;
      newEvent.target.value = inputMask.middleware(e.target.value || '');

      return onChange(newEvent);
    },
    [inputMask, mask, onChange],
  );

  // Filtrando propriedades que não podem ser enviadas para o <input />
  const fieldProps = useMemo(() => {
    const { componentRef, isRequired, isHTMLLabel, ...rest } = props;
    return rest;
  }, [props]);

  const { componentRef } = props;

  const middlewaredPlaceholder = useMemo(() => {
    if (mask === 'date') return '__/__/____';

    return placeholder;
  }, [mask, placeholder]);

  const field = useCallback(
    () => (
      <input
        {...fieldProps}
        ref={componentRef || null}
        value={inputMask.middleware(value || '')}
        type={inputType}
        disabled={disabled}
        autoComplete={autoComplete}
        onChange={handleOnChange}
        placeholder={middlewaredPlaceholder}
        min="1"
      />
    ),
    [
      fieldProps,
      componentRef,
      inputMask,
      value,
      inputType,
      disabled,
      autoComplete,
      handleOnChange,
      middlewaredPlaceholder,
    ],
  );

  return (
    <div>
      {label && (
        <Label>
          <p>{isHTMLLabel ? <>{templateLiteralToHTML(label)}</> : <>{label}</>}</p>

          {isRequired && (
            <p className="required">
              <span>*</span>
            </p>
          )}

          {tooltip && <InputTooltip label={tooltip} />}
        </Label>
      )}

      <Container
        hasError={error}
        htmlFor={id}
        onMouseEnter={() => setInputOnFocus(true)}
        onMouseLeave={() => setInputOnFocus(false)}
        className={`custom-input ${className || ''}`}
      >
        {icon || preText ? (
          <IconContainer hasError={error} isDisabled={disabled}>
            {field()}
            <div>{preText ? <PreText>{preText}</PreText> : <FontAwesomeIcon icon={icon} />}</div>
          </IconContainer>
        ) : (
          <>{field()}</>
        )}

        {type === 'password' && (
          <PasswordIconContainer
            hasLabel={!!label}
            type="button"
            onClick={() => {
              inputType === 'password' ? setInputType('text') : setInputType('password');
            }}
          >
            <FontAwesomeIcon icon={inputType === 'password' ? faEye : faEyeSlash} />
          </PasswordIconContainer>
        )}

        {fieldRules && inputOnFocus && (
          <FieldRulesContainer hasError={error}>
            <span>{fieldRules}</span>
          </FieldRulesContainer>
        )}
      </Container>

      {error && <Error>{error}</Error>}
    </div>
  );
};

export default CustomInput;
